// QR Kiosk v2 — keyboard-safe area (Round-6, root cause B). `keyboardInset` = how far
// the on-screen keyboard (plus any visual-viewport scroll) intrudes into the LAYOUT
// viewport, which is what position:fixed anchors to. Full-screen overlays reserve this
// at their bottom (paddingBottom) and bottom-sheets lift by it (bottom) so an in-flow
// action bar always rides above the keyboard.
//
//   resizes-content browsers: layout viewport shrinks with the keyboard → inset ≈ 0.
//   resizes-visual (Android Chrome default, no interactive-widget meta): the keyboard
//     shrinks ONLY the visual viewport; the layout viewport keeps full height →
//     inset = layoutH − vv.height − vv.offsetTop.
//
// offsetTop MUST be subtracted and the vv `scroll` event MUST be listened to: when
// Chrome scrolls the visual viewport to reveal a caret, the keyboard's overlap with the
// layout viewport shrinks by the scrolled amount — this self-correction is exactly what
// the old height-only hook (useVisualViewportHeight) was missing.
import { useEffect, useState } from "react";

export interface KeyboardSafeArea {
  /** Layout-viewport pixels currently covered by the keyboard (≥ 0). */
  keyboardInset: number;
  /** Visual-viewport height, or null when visualViewport is unavailable. */
  vvHeight: number | null;
}

export function useKeyboardInset(): KeyboardSafeArea {
  const [area, setArea] = useState<KeyboardSafeArea>({ keyboardInset: 0, vvHeight: null });

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;
    let raf = 0;
    const update = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const layoutH = document.documentElement.clientHeight;
        setArea({
          vvHeight: Math.round(vv.height),
          keyboardInset: Math.max(0, Math.round(layoutH - vv.height - vv.offsetTop)),
        });
      });
    };
    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(raf);
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, []);

  return area;
}

export default useKeyboardInset;
